/**
 * "What would I pay?" estimator (build F5 / Appendix A3).
 * Total = hostMonthlyCents(properties) + ownerPortalMonthlyCents(owners).
 */
import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { track } from "@/lib/marketing/analytics";
import { estimateBreakdown, formatUsd } from "@/lib/marketing/pricing-copy";

const MAX_INPUT = 10000;
/** Fire `pricing_estimator_used` once the visitor pauses typing. */
const TRACK_DELAY_MS = 800;

function toCount(raw: string): number {
  const n = Math.floor(Number(raw));
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.min(n, MAX_INPUT);
}

export function PricingEstimator({ initialProperties = 12 }: { initialProperties?: number }) {
  const [propertiesInput, setPropertiesInput] = useState(String(initialProperties));
  const [ownersInput, setOwnersInput] = useState("0");
  const touched = useRef(false);

  const properties = toCount(propertiesInput);
  const owners = toCount(ownersInput);
  const estimate = estimateBreakdown(properties, owners);

  useEffect(() => {
    if (!touched.current) return;
    const timer = window.setTimeout(() => {
      track("pricing_estimator_used", { properties, owners });
    }, TRACK_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [properties, owners]);

  return (
    <div
      className="rounded-2xl border border-gray-200 bg-white p-6 sm:p-8 shadow-sm max-w-3xl mx-auto"
      data-testid="section-estimator"
    >
      <h2 className="text-2xl font-bold text-gray-900">What would I pay?</h2>
      <div className="mt-6 grid gap-5 sm:grid-cols-2">
        <div>
          <Label htmlFor="estimator-properties" className="text-gray-800">
            Properties
          </Label>
          <Input
            id="estimator-properties"
            type="number"
            inputMode="numeric"
            min={0}
            max={MAX_INPUT}
            value={propertiesInput}
            onChange={(e) => {
              touched.current = true;
              setPropertiesInput(e.target.value);
            }}
            className="mt-2 bg-white text-gray-900"
            data-testid="input-estimator-properties"
          />
        </div>
        <div>
          <Label htmlFor="estimator-owners" className="text-gray-800">
            Owners (property managers only)
          </Label>
          <Input
            id="estimator-owners"
            type="number"
            inputMode="numeric"
            min={0}
            max={MAX_INPUT}
            value={ownersInput}
            onChange={(e) => {
              touched.current = true;
              setOwnersInput(e.target.value);
            }}
            className="mt-2 bg-white text-gray-900"
            data-testid="input-estimator-owners"
          />
        </div>
      </div>
      <div className="mt-6 rounded-xl bg-gray-50 p-5" aria-live="polite">
        <p className="text-3xl font-bold text-gray-900" data-testid="text-estimator-total">
          {formatUsd(estimate.totalCents)}/mo
        </p>
        <p className="mt-1 text-sm text-gray-600 break-words" data-testid="text-estimator-breakdown">
          {estimate.line}
        </p>
      </div>
    </div>
  );
}
